// Get DOM elements
const thumbnails = document.querySelectorAll('.carousel-container .thumbnail');
const arrows = document.querySelectorAll('.carousel-container .arrow');
const mainImageContainer = document.querySelector('.carousel-container .main-image-container');
const videoContainer = document.querySelector('.carousel-video-container');

// Configuration
const maxVisibleThumbnails = 4;

// Variables for carousel navigation
let currentIndex = 0;
let startX;
let endX;

// Initialize the carousel
initializeCarousel();

/**
 * Sets the given thumbnail as active and updates the main image and video.
 *
 * @param {Element} thumbnail The thumbnail to be set as active.
 */
function setActiveThumbnail(thumbnail) {
  toggleThumbnailActiveClass(thumbnail);
  setCarouselActiveMedia(thumbnail);
  toggleThumbnailDisplay(thumbnail);
}

/**
 * Adds the 'thumbnail-active' class to the given thumbnail and removes it from the others.
 *
 * @param {Element} thumbnail The selected thumbnail.
 */
function toggleThumbnailActiveClass(thumbnail) {
  thumbnails.forEach((_thumbnail) => _thumbnail.classList.remove('thumbnail-active'));
  thumbnail.classList.add('thumbnail-active');
}

/**
 * Sets the carousel active media (image or video) based on the given thumbnail.
 *
 * @param {Element} thumbnail The selected thumbnail.
 */
function setCarouselActiveMedia(thumbnail) {
  const videoEmbedCode = thumbnail.getAttribute('data-wistia-video');
  if (videoEmbedCode) {
    setVideo(videoEmbedCode);
  } else {
    setMainImage(thumbnail);
  }
}

/**
 * Sets the main image and its description.
 *
 * @param {Element} thumbnail The selected thumbnail.
 */
function setMainImage(thumbnail) {
  const mainImage = document.querySelector('.carousel-container .main-image');
  mainImage.src = thumbnail.src;
  mainImage.alt = thumbnail.alt;
  setVideoDisplay(false);
  setMainImageDisplay(true);
}

/**
 * Sets the video embed code.
 *
 * @param {string} videoEmbedCode The video embed code.
 */
function setVideo(videoEmbedCode) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(videoEmbedCode, 'text/html');
  appendScriptsToContainer(Array.from(doc.getElementsByTagName('script')), videoContainer);
  videoContainer.innerHTML = doc.querySelector('.wistia_responsive_padding').outerHTML;
  setVideoDisplay(true);
  setMainImageDisplay(false);
}

/**
 * Sets the display property of the video container.
 *
 * @param {boolean} displayVideo Whether the video should be displayed.
 */
function setVideoDisplay(displayVideo) {
  videoContainer.style.display = displayVideo ? 'block' : 'none';
}

/**
 * Sets the display property of the main image container.
 *
 * @param {boolean} displayMainImage Whether the main image should be displayed.
 */
function setMainImageDisplay(displayMainImage) {
  mainImageContainer.style.display = displayMainImage ? 'flex' : 'none';
}

/**
 * Appends the given scripts to the given container.
 *
 * @param {Array} scripts The scripts to be appended.
 * @param {Element} container The container to which the scripts will be appended.
 */
function appendScriptsToContainer(scripts, container) {
  scripts.forEach((script) => {
    const newScript = document.createElement('script');
    newScript.src = script.src;
    newScript.async = true;
    container.appendChild(newScript);
  });
}

/**
 * Gets the index of the thumbnail in the thumbnails array.
 *
 * @param {Element} thumbnail The selected thumbnail.
 *
 * @returns {number} The index of the thumbnail.
 */
function getThumbnailIndex(thumbnail) {
  return Array.from(thumbnails).indexOf(thumbnail);
}

/**
 * Toggles the visibility of thumbnails based on the scrolling direction.
 *
 * @param {Element} thumbnail The selected thumbnail.
 */
function toggleThumbnailDisplay(thumbnail) {
  const previousIndex = currentIndex;
  currentIndex = getThumbnailIndex(thumbnail);
  if (thumbnail.style.display !== 'none') {
    return;
  }

  thumbnail.style.display = 'block';
  const offset = previousIndex - currentIndex;
  let targetIndex = offset > 0 ? currentIndex + maxVisibleThumbnails : currentIndex - maxVisibleThumbnails;
  targetIndex = Math.min(Math.max(targetIndex, 0), thumbnails.length - 1);
  setThumbnailDisplay(targetIndex, 'none');
}

/**
 * Sets the 'display' property of the thumbnail at the given index.
 *
 * @param {number} index The index of the thumbnail.
 * @param {string} displayValue The value of the 'display' property.
 */
function setThumbnailDisplay(index, displayValue) {
  if (!thumbnails[index]) {
    return;
  }

  thumbnails[index].style.display = displayValue;
}

/**
 * Event handler for clicking on thumbnails.
 *
 * @param {Event} event The click event.
 */
function thumbnailClickHandler(event) {
  setActiveThumbnail(event.currentTarget);
}

/**
 * Event handler for clicking on arrows.
 *
 * @param {Event} event The click event.
 */
function arrowClickHandler(event) {
  const activeThumbnail = document.querySelector('.thumbnail.thumbnail-active');
  const isArrowRight = event.currentTarget.classList.contains('arrow-right');
  let nextThumbnail = isArrowRight ? activeThumbnail.nextElementSibling : activeThumbnail.previousElementSibling;
  if (!nextThumbnail) {
    nextThumbnail = isArrowRight ? thumbnails[0] : thumbnails[thumbnails.length - 1];
  }

  setActiveThumbnail(nextThumbnail);
}

/**
 * Initializes arrows by adding event listeners.
 */
function initializeArrows() {
  if (!arrows) {
    return;
  }

  arrows.forEach((arrow) => arrow.addEventListener('click', arrowClickHandler));
}

/**
 * Initializes the thumbnails by adding event listeners and hiding the ones that should not be visible.
 */
function initializeThumbnails() {
  if (!thumbnails) {
    return;
  }

  thumbnails.forEach((thumbnail) => {
    thumbnail.addEventListener('click', thumbnailClickHandler);
    const index = getThumbnailIndex(thumbnail);
    if (index >= maxVisibleThumbnails) {
      setThumbnailDisplay(index, 'none');
    }
  });
}

/**
 * Initializes the carousel by adding event listeners and initializing the thumbnails.
 */
function initializeCarousel() {
  if (!document.querySelectorAll('.carousel-container').length) {
    return;
  }

  initializeArrows();
  initializeThumbnails();
  enableSwipeToChangeActiveThumbnail(mainImageContainer);
  enableSwipeToChangeActiveThumbnail(videoContainer);
  setActiveThumbnail(thumbnails[currentIndex]);
}

/**
 * Enables swipe gestures to change the active thumbnail on a given container.
 *
 * @param {Element} container The container to which the touch listeners will be added.
 */
function enableSwipeToChangeActiveThumbnail(container) {
  if (!container) {
    return;
  }

  container.addEventListener('touchstart', captureStartingXCoordinate, false);
  container.addEventListener('touchmove', preventDefault, false);
  container.addEventListener('touchend', handleSwipeGesture, false);
}

/**
 * Captures the starting X coordinate of the touch gesture.
 *
 * @param {Event} event The touchstart event.
 */
function captureStartingXCoordinate(event) {
  startX = event.touches[0].clientX;
}

/**
 * Prevents the default behavior of the touchmove event.
 *
 * @param {Event} event The touchmove event.
 */
function preventDefault(event) {
  event.preventDefault();
}

/**
 * Handles the swipe gesture, clicking on the left or right arrow based on the swipe direction.
 *
 * @param {Event} event The touchend event.
 */
function handleSwipeGesture(event) {
  endX = event.changedTouches[0].clientX;
  const deltaX = startX - endX;
  const containerRect = this.getBoundingClientRect();
  const containerLeft = containerRect.left;
  const containerWidth = containerRect.width;
  const halfContainerWidth = containerWidth / 2;
  if (deltaX > 50 && endX <= containerLeft + halfContainerWidth) {
    document.querySelector('.carousel-container .arrow-right').click();
  } else if (deltaX < -50 && endX >= containerLeft + halfContainerWidth) {
    document.querySelector('.carousel-container .arrow-left').click();
  }
}
